import { message } from 'ant-design-vue'
import request, { IPage, IWorkspaceResponse, IListWorkspaceResponse } from '/@/api/http/request'
import { TaskType, TaskStatus, OutOfControlAction } from '/@/types/task'
import { WaylineType } from '/@/types/wayline'

const HTTP_PREFIX = '/wayline/api/v1'

// Get Wayline Files
export const getWaylineFiles = async function (wid: string, body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${wid}/waylines?order_by=${body.order_by}&page=${body.page}&page_size=${body.page_size}&search_key=${body.search_key ?? ''}` + (body.wayline_id ? `&wayline_id=${body.wayline_id}` : '' + `&type=${body.type ?? ''}`)
  const result = await request.get(url)
  return result.data
}

// Download Wayline File
export const downloadWaylineFile = async function (workspaceId: string, waylineId: string): Promise<any> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/${waylineId}/url`
  const result = await request.get(url, { responseType: 'blob' })
  if (result.data.type === 'application/json') {
    const reader = new FileReader()
    reader.onload = function (e) {
      const text = reader.result as string
      const result = JSON.parse(text)
      message.error(result.message)
    }
    reader.readAsText(result.data, 'utf-8')
  } else {
    return result.data
  }
}

// Delete Wayline File
export const deleteWaylineFile = async function (workspaceId: string, waylineId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/${waylineId}`
  const result = await request.delete(url)
  return result.data
}

export interface CreatePlan {
  name: string,
  file_id: string,
  dock_sn: string,
  task_type: TaskType, // 任务类型
  wayline_type: WaylineType, // 航线类型
  task_days: number[] // 执行任务的日期（秒）
  task_periods: number[][] // 执行任务的时间点（秒）
  rth_altitude: number // 相对机场返航高度 20 - 500
  out_of_control_action: OutOfControlAction // 失控动作
  min_battery_capacity?: number, // The minimum battery capacity of aircraft.
  min_storage_capacity?: number, // The minimum storage capacity of dock and aircraft.
}

// Create Wayline Job
export const createPlan = async function (workspaceId: string, plan: CreatePlan): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/flight-tasks`
  const result = await request.post(url, plan)
  return result.data
}

export interface Task {
  job_id: string,
  job_name: string,
  task_type: TaskType, // 任务类型
  file_id: string, // 航线文件id
  file_name: string, // 航线名称
  wayline_type: WaylineType, // 航线类型
  dock_sn: string,
  dock_name: string,
  workspace_id: string,
  username: string,
  begin_time: string,
  end_time: string,
  execute_time: string,
  completed_time: string,
  status: TaskStatus, // 任务状态
  progress: number, // 执行进度
  code: number, // 错误码
  rth_altitude: number // 相对机场返航高度 20 - 500
  out_of_control_action: OutOfControlAction // 失控动作
  media_count: number // 媒体数量
  uploading:boolean // 是否正在上传媒体
  uploaded_count: number // 已上传媒体数量
}

// Get Wayline Jobs
export const getWaylineJobs = async function (workspaceId: string, page: IPage): Promise<IListWorkspaceResponse<Task>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs?page=${page.page}&page_size=${page.page_size}&status=${page.status ? page.status : ''}&job_id=${page.job_id ? page.job_id : ''}`
  const result = await request.get(url)
  return result.data
}

export const getWaylineJobsNew = async function (body:object): Promise<IListWorkspaceResponse<Task>> {
  const url = `${HTTP_PREFIX}/workspaces/getJobListPageVo`
  const result = await request.post(url, body)
  return result.data
}

export interface DeleteTaskParams {
  job_id: string
}

//  删除机场任务
export async function deleteTask (workspaceId: string, params: DeleteTaskParams): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs`
  const result = await request.delete(url, {
    params: params
  })
  return result.data
}
export async function deleteTaskArr (body:object): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/jobsByWorkspaceId`
  const result = await request.post(url, body)
  return result.data
}

export enum UpdateTaskStatus {
  Suspend = 0, // 暂停
  Resume = 1, // 恢复
}
export interface UpdateTaskStatusBody {
  job_id: string
  status: UpdateTaskStatus
}

// 更新机场任务状态
export async function updateTaskStatus (workspaceId: string, body: UpdateTaskStatusBody): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${body.job_id}`
  const result = await request.put(url, {
    status: body.status
  })
  return result.data
}

// 导入航线文件
export const importKmzFile = async function (workspaceId: string, file: {}, workOrder:string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/file/upload`
  const result = await request.post(url, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  })
  return result.data
}
// 更新航线文件
export const updateKmzFile = async function (workspaceId: string, file: {}, wayline_id:string, workOrder:string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/file/update/${wayline_id}`
  const result = await request.post(url, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  })
  return result.data
}
// 媒体立即上传
export const uploadMediaFileNow = async function (workspaceId: string, jobId: string): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${jobId}/media-highest`
  const result = await request.post(url)
  return result.data
}
// 获取航线经纬度
export const getWaylineLngLat = async function (workspaceId: string, waylineId: string): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/${waylineId}`
  const result = await request.post(url)
  return result.data
}
// 获取航线经纬度
export const getWaylineInfo = async function (waylineId: string): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${waylineId}`
  const result = await request.post(url)
  return result.data
}
// 取消任务
export async function cancelJobIds (workspaceId: string, jobIds:string): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/cancelJobIds`
  const formData = new FormData()
  formData.append('jobIds', jobIds)
  const result = await request.post(url, formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    }
  )
  return result.data
}

// 统计飞行数据
export const getStatisticalFlight = async function (workspaceId: string, time:{begin_day:string, end_day:string, device_sn:string}): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/flightData`
  const result = await request.post(url, time)
  return result.data
}
// 算法和航线的绑定
export const addFlyAlgorithmModel = async function (body:{
  user_id: string,
  fly_line_id: string,
  task_no: string,
  model_name: string,
  model_no: string,
  open_task: number
}): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/waylines/addFlyAlgorithmModel`
  const result = await request.post(url, body)
  return result.data
}
// 算法和航线的删除
export const delFlyAlgorithmModel = async function (body:{
  id: string,
}): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/waylines/delFlyAlgorithmModel`
  const result = await request.post(url, body)
  return result.data
}
// 算法和航线的获取
export const getFlyAlgorithmTaskList = async function (body:{
  user_id: string,
  fly_line_id: string
}): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/waylines/getFlyAlgorithmTaskList`
  const result = await request.post(url, body)
  return result.data
}
// 未完成任务文件获取
export const getFlyTaskIncompleteFile = async function (workspaceId: string, jobIds:string): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/getFileList/${workspaceId}/${jobIds}`
  const result = await request.post(url)
  return result.data
}
// 删除指点飞行文件
export const deleteFlyTaskIncompleteFile = async function (workspace_id:string, job_id:string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/deleteFiles/${workspace_id}/${job_id}`
  const result = await request.post(url)
  return result.data
}

export const updateRcAiFlag = async function (body:object): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/updateRcAiFlag`
  const result = await request.post(url, body)
  return result.data
}
// kml文件导入
export const importXmlFile = async function (file: {}): Promise<IWorkspaceResponse<any>> {
  const url = '/api/kmz/upload'
  const result = await request.post(url, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  })
  return result.data
}
